import type { PaletteMode } from "@mui/material";
import {
  inputBaseClasses,
  inputLabelClasses,
  outlinedInputClasses,
} from "@mui/material";
import {
  type Components,
  createTheme,
  type Shadows,
  type Theme,
} from "@mui/material/styles";
import { useMemo } from "react";

import palette_Light_tokens from "./designTokens/palette.Light.tokens.json";
import palette_Dark_tokens from "./designTokens/palette.Dark.tokens.json";
import material_colors_tokens from "./designTokens/material_colors.Value.tokens.json";
import { aboveBottomBanners } from "@/components/banners/placements/bottomInsets";

declare module "@mui/material/styles" {
  interface PaletteColor {
    selected: string;
    focus: string;
    focusVisible: string;
  }

  interface GooglePalette {
    blue: string;
    green: string;
    red: string;
    yellow: string;
    blueShadow: string;
    blueDark: string;
    hoveredBorder: string;
  }

  interface PaypalPalette {
    blue: string;
    blueLight: string;
    goldDefault: string;
    goldHover: string;
  }

  interface PromoBannerPalette {
    background: string;
    ctaBackground: string;
    ctaHoverBackground: string;
  }

  interface BannerPalette {
    promoBackground: string;
    promoSecondaryText: string;
    promoIconBackground: string;
    ctaBackground: string;
    ctaHoverBackground: string;
    darkBackground: string;
    darkSecondaryText: string;
    darkIconBackground: string;
    subtleBackground: string;
    countdownColorBackground: string;
    countdownDigitBackground: string;
  }

  interface LocationInfoPalette {
    background: string;
    warningBackground: string;
  }

  interface Palette {
    neutral: Palette["primary"];
    tertiary: Palette["primary"];
    google: GooglePalette;
    paypal: PaypalPalette;
    promoBanner: PromoBannerPalette;
    banner: BannerPalette;
    locationInfo: LocationInfoPalette;
  }

  // allow configuration using `createTheme`
  interface PaletteOptions {
    neutral?: PaletteOptions["primary"];
    tertiary?: PaletteOptions["primary"];
    google?: Partial<GooglePalette>;
    paypal?: Partial<PaypalPalette>;
    promoBanner?: Partial<PromoBannerPalette>;
    banner?: Partial<BannerPalette>;
    locationInfo?: Partial<LocationInfoPalette>;
  }
}

// Update the Button's color prop options
declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    neutral: true;
    tertiary: true;
  }
}

declare module "@mui/material/IconButton" {
  interface IconButtonPropsColorOverrides {
    neutral: true;
    tertiary: true;
  }
}

// Banner colours that stay the same in light and dark mode (r-1783): the Promo
// green with its white text and tiles, and the Dark variant's white text and
// tiles. White-on-colour alphas come from the dark palette, whose white states
// are the ones Figma uses on a coloured surface in both modes.
const fixedBannerColors = {
  promoBackground: material_colors_tokens.teal["600"].value,
  promoSecondaryText: "rgba(255, 255, 255, 0.85)",
  promoIconBackground: palette_Dark_tokens.common.white.selected.value,
  ctaBackground: material_colors_tokens.lightGrey["0"].value,
  ctaHoverBackground: material_colors_tokens.lightGrey.A100.value,
  darkSecondaryText: palette_Dark_tokens.text.secondary.value,
  darkIconBackground: palette_Dark_tokens.common.white.focus.value,
  // The countdown's box on the Promo green and each digit's tile; Figma sets
  // them as raw alphas, with no token.
  countdownColorBackground: "rgba(0, 0, 0, 0.2)",
  countdownDigitBackground: "rgba(255, 255, 255, 0.1)",
};

export const lightPaletteOptions = {
  background: {
    default: "#F5F5F5",
  },
  primary: {
    main: "#1BB394",
    dark: "#008266",
    light: "#60E6C4",
    contrastText: "#FFFFFF",
    selected: palette_Light_tokens.primary.selected.value,
    focus: palette_Light_tokens.primary.focus.value,
    focusVisible: palette_Light_tokens.primary.focusVisible.value,
  },
  //Unused
  secondary: {
    main: "#B243C4",
    dark: "#8216AC",
    light: "#C166CF",
    contrastText: "#FFFFFF",
    selected: palette_Light_tokens.secondary.selected.value,
  },
  info: {
    main: "#1976D2",
    dark: "#0D47A1",
    light: "#2196F3",
    contrastText: "#FFFFFF",
    selected: palette_Light_tokens.common.info.selected.value,
  },
  warning: {
    main: "#FC9800",
    dark: "#EF6C00",
    light: "#FFB74D",
    contrastText: "#FFFFFF",
    selected: palette_Light_tokens.warning.selected.value,
  },
  error: {
    main: "#F44336",
    dark: "#C62828",
    light: "#EF9A9A",
    contrastText: "#FFFFFF",
    selected: palette_Light_tokens.error.selected.value,
  },
  success: {
    main: "#4CAF50",
    dark: "#2E7D32",
    light: "#66BB6A",
    contrastText: "#FFFFFF",
    selected: palette_Light_tokens.success.selected.value,
  },
  grey: {
    50: "#FAFAFA",
    100: "#F5F5F5",
    200: "#EEEEEE",
    300: "#E0E0E0",
    400: "#BDBDBD",
    500: "#9E9E9E",
    600: "#757575",
    700: "#616161",
    800: "#424242",
    900: "#212121",
  },
  neutral: {
    main: "#616161",
    contrastText: "#FFFFFF",
    selected: palette_Light_tokens.neutral.selected.value,
    focusVisible: palette_Light_tokens.neutral.focusVisible.value,
  },
  //Same as mui default
  text: {
    secondary: "rgba(0, 0, 0, 0.6)",
    selected: palette_Light_tokens.text.selected.value,
  },
  //Same as mui default
  action: {
    active: "rgba(0, 0, 0, 0.54)",
    main: "#e8e8e8",
    dark: "#cbcbcb",
    contrastText: "#1F1F1F",
    selected: palette_Light_tokens.action.selected.value,
  },
  google: {
    blue: "#4285F4",
    green: "#34A853",
    red: "#EA4335",
    yellow: "#FBBC04",
    blueShadow: "#BED2F5",
    blueDark: "#3467D6",
    // Figma `google/hoveredBorder`. The matching `google/enabledBorder` aliases
    // `divider`, so SocialButton uses palette.divider directly for that state.
    hoveredBorder: "rgba(0, 0, 0, 0.30)",
  },
  paypal: {
    blue: "#113984",
    blueLight: "#009EE3",
    goldDefault: "#FDC438",
    goldHover: "#E6B739",
  },
  // Promo banner keeps the same colors in light and dark mode (r-1783), so it
  // uses fixed primitives (teal/lightGrey) instead of mode-dependent slots —
  // the palette grey scale inverts in dark mode and would flip the CTA.
  promoBanner: {
    background: material_colors_tokens.teal["600"].value,
    ctaBackground: material_colors_tokens.lightGrey["0"].value,
    ctaHoverBackground: material_colors_tokens.lightGrey.A100.value,
  },
  banner: {
    ...fixedBannerColors,
    darkBackground: material_colors_tokens.lightGrey["900"].value,
    subtleBackground: material_colors_tokens.lightGrey["0"].value,
  },
  // Location-info chips (e.g. product tab statuses) keep their fixed Figma
  // primitives (teal.100 / yellow.200) in both modes, like the promo banner.
  locationInfo: {
    background: material_colors_tokens.teal["100"].value,
    warningBackground: material_colors_tokens.yellow["200"].value,
  },
};

export const darkPaletteOptions = {
  background: {
    paper: "#121212",
    default: "#121212",
    defaultChannel: "18 18 18",
  },
  primary: {
    main: "#60E6C4",
    dark: "#46AD93",
    light: "#60E6C4",
    contrastText: "#1F1F1F",
    selected: palette_Dark_tokens.primary.selected.value,
    focus: palette_Dark_tokens.primary.focus.value,
    focusVisible: palette_Dark_tokens.primary.focusVisible.value,
  },
  secondary: {
    main: "#D393DD",
    dark: "#C166CF",
    light: "#E4BEEA",
    contrastText: "#1F1F1F",
    selected: palette_Dark_tokens.secondary.selected.value,
  },
  warning: {
    main: "#FDCC81",
    dark: "#F57C00",
    light: "#FFB74D",
    contrastText: "#202020",
    selected: palette_Dark_tokens.warning.selected.value,
  },
  success: {
    main: "#A5D6A7",
    dark: "#388E3C",
    light: "#81C784",
    contrastText: "#000000",
    selected: palette_Dark_tokens.success.selected.value,
  },
  info: {
    main: "#90CAF9",
    dark: "#1488D1",
    light: "#4FC3F7",
    contrastText: "#000000",
    selected: palette_Dark_tokens.common.info.selected.value,
  },
  error: {
    main: "#EF9A9A",
    dark: "#D32F2F",
    light: "#E57373",
    contrastText: "#000000",
    selected: palette_Dark_tokens.error.selected.value,
  },
  grey: {
    50: "#212121",
    100: "#2B2B2B",
    200: "#353535",
    300: "#3F3F3F",
    400: "#535353",
    500: "#676767",
    600: "#999999",
    700: "#B7B7B7",
    800: "#DFDFDF",
    900: "#FDFDFD",
  },
  neutral: {
    main: "#D9D9D9",
    dark: "#9C9C9C",
    light: "#F5F5F5",
    contrastText: "#1F1F1F",
    selected: palette_Dark_tokens.neutral.selected.value,
    focusVisible: palette_Dark_tokens.neutral.focusVisible.value,
  },
  //Same as mui default
  text: {
    secondary: "rgba(255, 255, 255, 0.7)",
    selected: palette_Dark_tokens.text.selected.value,
  },
  //Same as mui default
  action: {
    active: "#fff",
    main: "#313131",
    dark: "#616161",
    contrastText: "#fff",
    selected: palette_Dark_tokens.action.selected.value,
  },
  google: {
    blue: "#4285F4",
    green: "#34A853",
    red: "#EA4335",
    yellow: "#FBBC04",
    blueShadow: "#BED2F5",
    blueDark: "#3467D6",
    hoveredBorder: "rgba(255, 255, 255, 0.30)",
  },
  paypal: {
    blue: "#2E4FC2",
    goldDefault: "#FDC438",
    goldHover: "#E6B739",
  },
  // Same fixed colors as light mode by design (r-1783).
  promoBanner: {
    background: material_colors_tokens.teal["600"].value,
    ctaBackground: material_colors_tokens.lightGrey["0"].value,
    ctaHoverBackground: material_colors_tokens.lightGrey.A100.value,
  },
  banner: {
    ...fixedBannerColors,
    darkBackground: material_colors_tokens.darkGrey["50"].value,
    subtleBackground: material_colors_tokens.darkGrey["50"].value,
  },
  // Location-info chips (e.g. product tab statuses) keep their fixed Figma
  // primitives (teal.100 / yellow.200) in both modes, like the promo banner.
  locationInfo: {
    background: material_colors_tokens.teal["100"].value,
    warningBackground: material_colors_tokens.yellow["200"].value,
  },
};

export const defaultShadows = createTheme({}).shadows;

export const lightShadows: Shadows = [
  ...(defaultShadows.map((shadow, i) => {
    //Make first elevation one for cards.
    if (i === 1) {
      return "none";
    }
    if (i === 2) {
      return "0px 12px 24px -4px rgba(158, 158, 158, 0.25)";
    }
    return shadow;
  }) as Shadows),
];

export const themeOptions = {
  // spacing: [0, 4, 8, 16, 24, 32, 64],
  spacing: (factor: number) => {
    const values = [0, 4, 8, 16, 24, 32, 64, 128, 256, 512, 1024, 2048];
    const index = Math.floor(factor);
    const currentSpace = values[index];
    const nextSpace = values[index + 1] || currentSpace * 2;
    const space = currentSpace + (nextSpace - currentSpace) * (factor - index);
    return `${space}px`;
  },
  typography: {
    body2: {
      fontWeight: 400,
      fontSize: 14,
      lineHeight: "20px",
      letterSpacing: "0.17px",
    },
    h5: {
      fontWeight: 600,
      fontSize: 24,
      lineHeight: "32px",
      letterSpacing: "0.17px",
    },
    button: {
      textTransform: "none" as const,
    },
  },
  components: {
    MuiCard: {
      defaultProps: {
        elevation: 1,
      },
    },
  },
};

/**
 * Chrome fills saved credentials after first paint without firing an event, so
 * MUI's dirty flag — computed once on mount — leaves the label over the text.
 * CSS sees the browser's fill state when JavaScript cannot.
 */
const MuiTextField = {
  styleOverrides: {
    root: {
      [`&:has(.${inputBaseClasses.input}:-webkit-autofill)`]: {
        [`& .${inputLabelClasses.root}`]: {
          transform: "translate(14px, -9px) scale(0.75)",
          // The fill lands after first paint; without this it visibly slides up.
          transition: "none",
        },
        [`& .${outlinedInputClasses.notchedOutline} legend`]: {
          maxWidth: "100%",
        },
      },
    },
  },
};

/**
 * A snackbar at the bottom of the screen rises above the Sticky bottom bar
 * and the Slide-in while either shows, from MUI's own 8px (24px from `sm`).
 */
const MuiSnackbar: Components<Theme>["MuiSnackbar"] = {
  styleOverrides: {
    root: ({ ownerState, theme }) =>
      ownerState.anchorOrigin?.vertical === "bottom"
        ? {
            bottom: aboveBottomBanners(8),
            [theme.breakpoints.up("sm")]: { bottom: aboveBottomBanners(24) },
          }
        : {},
  },
};

const getDesignTokens = (mode: PaletteMode) => ({
  ...themeOptions,
  // Overriding components theme defaults
  ...(mode === "light"
    ? {
        components: {
          MuiTextField,
          MuiSnackbar,
          MuiSwitch: {
            styleOverrides: {
              thumb: () => ({
                boxShadow: defaultShadows[1],
              }),
            },
          },
          MuiSelect: {
            defaultProps: {
              sx: {
                backgroundColor: "#fff",
              },
            },
          },
        },
      }
    : {
        components: {
          MuiTextField,
          MuiSnackbar,
          MuiSelect: {
            defaultProps: {
              sx: {
                backgroundColor: "#1F1F1F",
              },
            },
          },
        },
      }),
  palette:
    mode === "light"
      ? { mode, ...lightPaletteOptions }
      : { mode, ...darkPaletteOptions },
  shadows: mode === "dark" ? defaultShadows : lightShadows,
});

export function useAppTheme(mode: PaletteMode) {
  return useMemo(() => createTheme(getDesignTokens(mode)), [mode]);
}
